<script lang="ts">
    import { onMount } from 'svelte';
    import SlidingModal from '@amp/web-app-components/src/components/Modal/SlidingModal.svelte';
    import NowPlayingBackdrop from '@amp/web-app-components/src/components/NowPlayingBackdrop/NowPlayingBackdrop.svelte';
    import { buildMediaQueryStore } from '@amp/web-app-components/src/stores/media-query';
    import {
        MOBILE_NOW_PLAYING_LANDSCAPE_ASPECT_RATIO,
        MOBILE_NOW_PLAYING_LANDSCAPE_MAX_HEIGHT,
    } from '@amp/web-app-components/src/components/MobileNowPlaying/constants';

    /**
     * html element that toggles the full screen modal visibility
     * @type {HTMLElement}
     */
    export let modalTriggerElement: HTMLElement;

    /**
     * determine if sliding animation should be disabled
     * @type {boolean}
     */
    export let disableSlide: boolean;

    /**
     * determine if we should render the "playback-item" slot
     * @type {boolean}
     */
    export let isPlaybackItemVisible: boolean;

    /**
     * determine if we should render the "text" slot
     * @type {boolean}
     */
    export let isTextVisible: boolean;

    /**
     * determine if we should render the "queue" slot
     * @type {boolean}
     */
    export let isQueueVisible: boolean;

    /**
     * determine if we should render the "info" slot
     * @type {boolean}
     */
    export let isInfoVisible: boolean = false;

    /**
     * url to media artwork.
     * passed to NowPlayingBackdrop component to generate background animation.
     * @type {string}
     */
    export let artworkURL: string | null = null;

    /**
     * determine if a nowPlayingItem exists (to be used in NowPlayingBackdrop styles)
     * @type {boolean}
     */
    export let hasNowPlayingItem: boolean;

    /**
     * determine if now playing item is a video (to be used in NowPlayingBackdrop styles)
     * @type {boolean}
     */
    export let isVideo: boolean = false;

    /**
     * accessiblity text for SlidingModal
     * @type {string}
     */
    export let ariaLabel: string;

    /**
     * function used in close button for translations
     * @type {function}
     */
    export let translateFn: (key: string) => string;

    onMount(() => {
        // Get the <dialog> element mounted in this component and set an ID
        // so that child components can query for it if needed.
        const dialogElement: HTMLDialogElement = document.querySelector(
            '.modal-wrapper dialog',
        );
        if (dialogElement) {
            dialogElement.id = 'mobile-now-playing-dialog';
        }
    });

    const nowPlayingItemLayout = buildMediaQueryStore('large', {
        landscape: `(min-aspect-ratio: ${MOBILE_NOW_PLAYING_LANDSCAPE_ASPECT_RATIO}) and (max-height: ${MOBILE_NOW_PLAYING_LANDSCAPE_MAX_HEIGHT}px)`,
        large: '', // Fall back to 'large' when 'landscape' MQ is unmatched
    });

    $: useLandscapeLayout = $nowPlayingItemLayout === 'landscape';
</script>

<div
    id="mobile-now-playing"
    class="modal-wrapper"
    data-testid="mobile-now-playing"
>
    <SlidingModal
        {modalTriggerElement}
        {disableSlide}
        {ariaLabel}
        {translateFn}
        disableScrim={true}
        on:close
    >
        <div
            slot="backdrop"
            data-testid="now-playing-backdrop"
            class="backdrop"
            class:backdrop--video={isVideo}
            class:backdrop--empty={!hasNowPlayingItem}
        >
            {#if hasNowPlayingItem && !isVideo}
                <div class="dynamic-backdrop">
                    <NowPlayingBackdrop {artworkURL} --text-border-radius="0" />
                </div>
            {/if}
        </div>

        <div
            class="now-playing-structure"
            class:now-playing-structure--queue={isQueueVisible}
            class:now-playing-structure--info={isInfoVisible}
            class:now-playing-structure--text={isTextVisible}
            class:now-playing-structure--playback-item={isPlaybackItemVisible}
            class:now-playing-structure--landscape={useLandscapeLayout}
        >
            <div class="now-playing-main">
                {#if isPlaybackItemVisible && $$slots['playback-item']}
                    <slot name="playback-item" />
                {/if}

                {#if isQueueVisible && $$slots['queue']}
                    <slot name="queue" />
                {/if}

                {#if isInfoVisible && $$slots['info']}
                    <slot name="info" />
                {/if}

                {#if isTextVisible && $$slots['text']}
                    <slot name="text" />
                {/if}
            </div>

            {#if $$slots['playback-controls']}
                <div class="playback-controls">
                    <slot name="playback-controls" />
                </div>
            {/if}

            {#if $$slots['now-playing-toggle']}
                <div class="now-playing-toggle">
                    <slot name="now-playing-toggle" />
                </div>
            {/if}
        </div>
    </SlidingModal>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;

    $modal-collapse-button-height: 51px;
    $progress-bar-height: 36px;

    // Approximate container heights (allowed to adjust with flex) borrowed from Android Now Playing spec
    $now-playing-main-height: 65.5%;
    $playback-controls-height: 34.5%;
    $now-playing-toggle-height: 54px;

    .modal-wrapper {
        --slidingModalHandleColor: var(--systemTertiary-onDark);

        // Do not display modal on viewport > 1000px wide.
        // This modal should be unmounted at this breakpoint by the parent FullscreenPlayer!
        // Including `display: none` prevents a flash of the modal resizing before unmounting,
        // and prevents issues with the UI freezing during the transition.
        @media (--small) {
            display: none;
        }
    }

    .backdrop {
        width: 100%;
        height: 100%;
    }

    .backdrop--video {
        background: black;
    }

    // Empty state background -- renders when NowPlayingBackdrop does not
    .backdrop--empty {
        background: var(--systemGray);
    }

    .now-playing-structure {
        height: calc(100dvh - #{$modal-collapse-button-height});
        display: flex;
        flex-direction: column;
        color: white;
        // Scrim to improve `mix-blend-mode` visibility on toggle-buttons
        background: linear-gradient(
            to top,
            rgba(0, 0, 0, 0.4) 0%,
            transparent 50%
        );

        // Fallback styles for browsers that don't support dvh units
        @supports not (height: 100dvh) {
            // Fill flex parent container
            height: 100%;

            // Subtract 54px from max-height to allow extra space for browser chrome
            max-height: calc(100vh - #{$modal-collapse-button-height} - 54px);
        }
    }

    .now-playing-main {
        // Allow main area to grow or shrink around its ideal height, depending
        // on available vertical space
        flex: 1 #{$now-playing-main-height};

        // Overflow must be set for children to size relative to this
        overflow: hidden auto;

        &::-webkit-scrollbar {
            display: none;
        }

        .now-playing-structure--queue &,
        .now-playing-structure--text & {
            flex: 1 calc(#{$now-playing-main-height} + #{$progress-bar-height}); // Adjust for missing progress bar
        }

        .now-playing-structure--queue &,
        .now-playing-structure--info & {
            overflow: hidden; // Delegate overflow scroll to queue container (needed for sticky header)
            mask-image: linear-gradient(
                to top,
                rgba(0, 0, 0, 0),
                rgba(0, 0, 0, 1) var(--fade-mask-height, 70px),
                rgba(0, 0, 0, 1)
            );
        }
    }

    .playback-controls {
        // Allow controls to shrink from their ideal height, depending on available vertical space.
        // The high `flex-shrink` value ensures that flex prioritizes shrinking this area *before*
        // shrinking the `.now-playing-main` area. (That is, the main area will only shrink when
        // the controls area cannot be collapsed any further.)
        flex: 0 100
            calc(#{$playback-controls-height} - #{$now-playing-toggle-height});
        margin: 0 32px;

        @include rtl {
            // Lock order of controls to LTR direction in RTL direction
            direction: ltr;
        }
    }

    .now-playing-toggle {
        margin: auto 32px 20px;
    }

    .now-playing-structure--landscape {
        display: grid;
        grid-template-rows: 1fr auto;
        gap: 20px;
        padding-bottom: 20px;

        .now-playing-main {
            // Bump artwork up slightly to make more room for controls
            margin-top: -12px;
        }

        .now-playing-toggle {
            display: none;
        }
    }

    // High contrast styles
    @media (prefers-contrast: more) {
        // Revert handle color so it's visible on page background
        .modal-wrapper {
            --slidingModalHandleColor: var(--systemQuaternary);
        }

        .now-playing-structure {
            color: var(--systemPrimary);
            background: transparent; // Remove mix-blend-mode scrim
        }

        .backdrop--empty {
            background: var(--pageBG);
        }

        // Hide artwork-based backdrop
        .dynamic-backdrop {
            display: none;
        }
    }
</style>
